import { theme } from '@Guidelines/Theme/theme';
import requestDocumentPanel from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Helpers/requestDocumentPanel';
import useDocumentViewer from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/useDocumentViewer';
import type { DocumentPanelTarget } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/documentPanelSignal';
import type DocumentFieldView from '@UI/Store/Documents/Views/DocumentFieldView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Button, Tag } from '@verclam/ui-kit';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

const panelStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const titleRowStyle = css`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem;
`;

const titleStyle = css`
	margin: 0;
	${theme.typography.cardTitle}
	color: ${theme.colors.ink};
`;

const valueStyle = css`
	padding: 0.875rem 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.large};
	font-weight: ${theme.fontWeights.medium};
	line-height: 1.5;
	color: ${theme.colors.ink};
	white-space: pre-wrap;
`;

const sourcesStyle = css`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.25rem;
`;

const captionStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const noteStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly target: Extract<DocumentPanelTarget, { readonly type: 'computed' }>;
};

type SourceLinkProps = {
	readonly field: DocumentFieldView;
};

const ComputedSourceLink: React.FC<SourceLinkProps> = ({ field }) => {
	const handleOpen = React.useCallback(() => {
		requestDocumentPanel({ type: 'field', fieldKey: field.key, occurrenceKey: null });
	}, [field.key]);

	return <Button type="button" link label={field.label} onClick={handleOpen} />;
};

/**
 * A computed field: read-only, its printed value and the fields it is computed from. The value is
 * what the viewer shows (`text` of the field's first occurrence); a source the document does not
 * know is left out.
 */
const ComputedFieldPanel: React.FC<Props> = ({ target }) => {
	const { handleRef, isViewerReady } = useDocumentViewer();
	const document = useSelector((state) => state.documentsStore.document);
	const [printedValue, setPrintedValue] = React.useState<string | null>(null);

	// The viewer applies a new document in a layout effect, so reading it here sees the updated value.
	React.useEffect(() => {
		if (document === null || !isViewerReady) {
			setPrintedValue(null);
			return;
		}

		const info = handleRef.current?.fields().find((field) => field.id === target.fieldId);
		setPrintedValue(info?.text ?? null);
	}, [handleRef, target.fieldId, document, isViewerReady]);

	const sources = React.useMemo(() => {
		if (document === null) {
			return [];
		}

		return document.orderedFields().filter((field) => target.dependsOnIds.includes(field.uuid));
	}, [document, target.dependsOnIds]);

	return (
		<div css={panelStyle}>
			<div css={titleRowStyle}>
				<h3 css={titleStyle}>{target.label}</h3>
				<Tag value="Calculé" severity="secondary" />
			</div>
			<div css={valueStyle}>{printedValue ?? '—'}</div>
			<ConditionalLoader data={sources.length > 0}>
				<div css={sourcesStyle}>
					<span css={captionStyle}>Calculé à partir de :</span>
					{sources.map((field) => (
						<ComputedSourceLink key={field.key} field={field} />
					))}
				</div>
			</ConditionalLoader>
			<p css={noteStyle}>Ce champ ne se modifie pas : modifiez les champs dont il dépend.</p>
		</div>
	);
};

export default ComputedFieldPanel;
